import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminMonitorSignalExits,
  adminScanLiveSignals,
  closeSignalPosition,
  createSignalPosition,
  fetchSignalFeed,
  fetchSignalsBacktests,
  fetchSignalPositions,
  fetchSignalPreferences,
  fetchSignalStrategyParams,
  fetchSignalStrategies,
  updateSignalStrategyParams,
  updateSignalPreferences,
} from "../api";
import { useAuth } from "../components/AuthGate";
import { prettyCompetitionName } from "../utils/competitionNames";
import { useLocation } from "react-router-dom";

const DEFAULT_STRATEGY = "__all_active__";
const PRIMARY_STRATEGY = "FOOTBALL_10_PRESSURE_DRAW";

const S = {
  page: { maxWidth: 1100, margin: "0 auto", color: "#eaf4ed" },
  hero: {
    border: "1px solid rgba(255,255,255,.1)",
    borderRadius: 14,
    padding: 18,
    background: "linear-gradient(175deg, rgba(16,31,22,0.96), rgba(12,23,17,0.96))",
    boxShadow: "0 10px 24px rgba(0,0,0,.18)",
    marginBottom: 14,
  },
  title: { fontSize: 28, fontWeight: 800, marginBottom: 6, letterSpacing: "-0.01em" },
  sub: { fontSize: 14, opacity: 0.9, lineHeight: 1.5 },
  row: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  controls: {
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 12,
    padding: 12,
    background: "rgba(0,0,0,.18)",
    marginBottom: 14,
  },
  select: {
    height: 38,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.16)",
    background: "rgba(255,255,255,.96)",
    padding: "0 10px",
    minWidth: 220,
  },
  btn: {
    height: 38,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.2)",
    background: "rgba(255,255,255,.92)",
    color: "#04130c",
    padding: "0 12px",
    fontWeight: 650,
    cursor: "pointer",
  },
  btnPrimary: {
    height: 38,
    borderRadius: 10,
    border: "1px solid #15c971",
    background: "linear-gradient(180deg, #31d48b, #16b96b)",
    color: "#052014",
    padding: "0 12px",
    fontWeight: 760,
    cursor: "pointer",
  },
  section: {
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 12,
    background: "linear-gradient(180deg, rgba(17,30,22,0.95), rgba(14,24,18,0.95))",
    boxShadow: "0 8px 20px rgba(0,0,0,.14)",
    padding: 14,
    marginBottom: 14,
  },
  sectionTitle: { fontSize: 18, fontWeight: 760, marginBottom: 10, letterSpacing: "-0.01em" },
  grid: { display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" },
  card: {
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 12,
    background: "rgba(255,255,255,.03)",
    padding: 12,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.18)",
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 700,
  },
  kv: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6, marginTop: 10, fontSize: 12 },
  muted: { opacity: 0.74 },
  flash: (ok) => ({
    border: `1px solid ${ok ? "rgba(52,211,153,.5)" : "rgba(248,113,113,.45)"}`,
    background: ok ? "rgba(5,150,105,.18)" : "rgba(153,27,27,.2)",
    borderRadius: 10,
    padding: "9px 11px",
    fontSize: 13,
    marginBottom: 10,
  }),
  tabs: { display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" },
  tab: (active) => ({
    height: 36,
    borderRadius: 999,
    border: `1px solid ${active ? "rgba(49,212,139,.8)" : "rgba(255,255,255,.2)"}`,
    background: active ? "rgba(22,185,107,.2)" : "transparent",
    color: active ? "#d2f9e7" : "#dce9e0",
    padding: "0 12px",
    cursor: "pointer",
    fontWeight: 650,
  }),
  input: {
    height: 38,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.18)",
    background: "rgba(255,255,255,.95)",
    padding: "0 10px",
    color: "#0a1f14",
  },
  textarea: {
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.18)",
    background: "rgba(255,255,255,.95)",
    padding: "8px 10px",
    color: "#0a1f14",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.52)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: 14,
  },
  modal: {
    width: "100%",
    maxWidth: 640,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.14)",
    background: "linear-gradient(180deg, rgba(17,30,22,.98), rgba(12,22,17,.98))",
    boxShadow: "0 14px 30px rgba(0,0,0,.4)",
    padding: 14,
  },
};

const toArray = (v) => (Array.isArray(v) ? v : []);
const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);
const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));
const fmtNum = (n, dp = 2) => {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(dp) : "—";
};
const fmtPct = (n, dp = 1) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const pct = Math.abs(x) <= 1 ? x * 100 : x;
  return `${pct.toFixed(dp)}%`;
};
const normFav = (v) => {
  const s = String(v || "").trim();
  if (!s) return "Unknown";
  if (s.toLowerCase() === "level") return "Level";
  return s;
};
const diffColor = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "#eaf4ed";
  if (Math.abs(v) < 0.05) return "#cbd5e1";
  if (v > 0) return "#34d399";
  return "#f59e0b";
};

function normalizeSignalRow(raw = {}) {
  const fixture = raw.fixture || {};
  const stats = raw.stats || {};
  const xgDiff = Number(stats?.xg_diff_favourite_minus_opponent ?? 0);
  const sot15Diff = Number(stats?.sot_rolling_15_diff_favourite_minus_opponent ?? 0);
  const pressureDiff = Number(stats?.pressure_diff_favourite_minus_opponent ?? 0);
  return {
    id: raw.signal_id ?? raw.id ?? null,
    fixture_id: raw.fixture_id ?? fixture.fixture_id ?? fixture.id ?? null,
    home_team: raw.home_team ?? fixture.home_team ?? fixture.home ?? "Home",
    away_team: raw.away_team ?? fixture.away_team ?? fixture.away ?? "Away",
    league: raw.league ?? raw.league_name ?? fixture.league ?? fixture.comp ?? raw.competition ?? "League",
    minute: raw.minute ?? raw.elapsed_minute ?? fixture.minute ?? fixture.elapsed ?? null,
    scoreline:
      raw.scoreline ??
      (raw.home_score != null && raw.away_score != null
        ? `${raw.home_score}-${raw.away_score}`
        : fixture.scoreline ?? "0-0"),
    recommended_outcome: raw.recommended_outcome ?? raw.outcome_key ?? "draw",
    recommended_platform: raw.recommended_platform ?? raw.platform ?? "Polymarket",
    market_price: raw.market_price ?? raw.current_price ?? raw.price ?? null,
    model_true_probability: raw.model_true_probability ?? raw.true_probability ?? raw.model_prob ?? null,
    edge: raw.edge ?? raw.edge_pct ?? null,
    confidence: raw.confidence ?? raw.confidence_pct ?? null,
    confidence_score: raw.confidence_score ?? null,
    data_quality: raw.data_quality ?? null,
    latency_ms: raw.latency_ms ?? null,
    strategy_id: raw.strategy_id ?? null,
    strategy_key: raw.strategy_key ?? null,
    favourite_team_name: stats?.favourite_team_name ?? null,
    xg_favors_team: stats?.xg_favors_team ?? null,
    sot15_favors_team: stats?.sot15_favors_team ?? null,
    market_url: raw.market_url ?? raw.url ?? null,
    market_id: raw.market_id ?? null,
    progress: raw.progress ?? null,
    reason: raw.reason ?? raw.watch_reason ?? raw.trigger_reason ?? "",
    xg_diff: Number.isFinite(xgDiff) ? xgDiff : 0,
    sot15_diff: Number.isFinite(sot15Diff) ? sot15Diff : 0,
    pressure_proxy: Number.isFinite(pressureDiff) ? pressureDiff : 0,
  };
}

export default function SignalsPage() {
  const { user, isAdmin, isPremium } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("feed");
  const [strategyKey, setStrategyKey] = useState(DEFAULT_STRATEGY);
  const [strategies, setStrategies] = useState([]);

  const [feed, setFeed] = useState({ open_signals: [], watchlist: [] });
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedErr, setFeedErr] = useState("");
  const [feedWarning, setFeedWarning] = useState("");

  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsErr, setPositionsErr] = useState("");
  const [closeFormById, setCloseFormById] = useState({});

  const [prefs, setPrefs] = useState({
    push_enabled: false,
    email_enabled: false,
    min_edge: 0,
    default_target_exit: "",
    favorite_sports: ["football"],
    favorite_strategies: [DEFAULT_STRATEGY],
  });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsErr, setPrefsErr] = useState("");
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSnapshot, setPrefsSnapshot] = useState(null);

  const [flash, setFlash] = useState(null);
  const [actionSummary, setActionSummary] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [notifyPremiumOnScan, setNotifyPremiumOnScan] = useState(true);
  const [includeAllLeaguesOnScan, setIncludeAllLeaguesOnScan] = useState(false);
  const [maxStalenessSec, setMaxStalenessSec] = useState(180);
  const [includeStoppedFixtures, setIncludeStoppedFixtures] = useState(false);
  const [refreshingCardKey, setRefreshingCardKey] = useState("");

  const [logModal, setLogModal] = useState(null);
  const [logSaving, setLogSaving] = useState(false);

  const [adminStrategyKey, setAdminStrategyKey] = useState(PRIMARY_STRATEGY);
  const [strategyParams, setStrategyParams] = useState([]);
  const [strategyParamsLoading, setStrategyParamsLoading] = useState(false);
  const [strategyParamsSaving, setStrategyParamsSaving] = useState(false);
  const [strategyParamsErr, setStrategyParamsErr] = useState("");

  const [backtestsDaysBack, setBacktestsDaysBack] = useState(90);
  const [backtestsStrategy, setBacktestsStrategy] = useState("all");
  const [backtestsRows, setBacktestsRows] = useState([]);
  const [backtestsLoading, setBacktestsLoading] = useState(false);
  const [backtestsErr, setBacktestsErr] = useState("");

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 2800);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("signals_scan_controls_v1");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Number.isFinite(Number(parsed?.max_staleness_sec))) {
        setMaxStalenessSec(Number(parsed.max_staleness_sec));
      }
      if (typeof parsed?.include_stopped === "boolean") {
        setIncludeStoppedFixtures(parsed.include_stopped);
      }
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        "signals_scan_controls_v1",
        JSON.stringify({
          max_staleness_sec: maxStalenessSec,
          include_stopped: includeStoppedFixtures,
        })
      );
    } catch {
      // noop
    }
  }, [maxStalenessSec, includeStoppedFixtures]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const requested = (params.get("tab") || "").toLowerCase();
    if (requested === "backtests" && isAdmin) setTab("admin");
  }, [location.search, isAdmin]);

  const loadStrategies = useCallback(async () => {
    try {
      const data = await fetchSignalStrategies();
      const rows = toArray(data?.strategies || data);
      setStrategies(rows);
      if (!rows.some((r) => (r.key || r.strategy_key) === adminStrategyKey)) {
        const fallback = rows[0]?.key || rows[0]?.strategy_key || PRIMARY_STRATEGY;
        setAdminStrategyKey(fallback);
      }
    } catch {
      setStrategies([]);
    }
  }, [adminStrategyKey]);

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    setFeedErr("");
    setFeedWarning("");
    try {
      const data = await fetchSignalFeed({
        sport: "football",
        strategy_key: strategyKey || DEFAULT_STRATEGY,
        include_watchlist: 1,
      });
      setFeed({
        open_signals: toArray(data?.open_signals).map(normalizeSignalRow),
        watchlist: toArray(data?.watchlist).map(normalizeSignalRow),
      });
      setFeedWarning(String(data?.warning || ""));
    } catch (e) {
      setFeedErr(e?.response?.data?.detail || "Could not load signal feed.");
      setFeed({ open_signals: [], watchlist: [] });
    } finally {
      setFeedLoading(false);
    }
  }, [strategyKey]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    setPositionsLoading(true);
    setPositionsErr("");
    try {
      const data = await fetchSignalPositions();
      const rows = toArray(data?.positions || data);
      setPositions(rows);
    } catch (e) {
      setPositionsErr(e?.response?.data?.detail || "Could not load your positions.");
      setPositions([]);
    } finally {
      setPositionsLoading(false);
    }
  }, [user]);

  const loadPrefs = useCallback(async () => {
    if (!user) return;
    setPrefsLoading(true);
    setPrefsErr("");
    try {
      const data = await fetchSignalPreferences();
      const merged = {
        push_enabled: !!data?.push_enabled,
        email_enabled: !!data?.email_enabled,
        min_edge: Number(data?.min_edge ?? 0),
        default_target_exit:
          data?.default_target_exit === undefined || data?.default_target_exit === null
            ? null
            : Number(data.default_target_exit),
        favorite_sports: toArray(data?.favorite_sports || ["football"]),
        favorite_strategies: toArray(data?.favorite_strategies || [DEFAULT_STRATEGY]),
      };
      setPrefs((prev) => ({
        ...prev,
        ...data,
        favorite_sports: toArray(data?.favorite_sports || prev.favorite_sports),
        favorite_strategies: toArray(data?.favorite_strategies || prev.favorite_strategies),
      }));
      setPrefsSnapshot(JSON.stringify(merged));
    } catch (e) {
      setPrefsErr(e?.response?.data?.detail || "Could not load signal preferences.");
    } finally {
      setPrefsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadStrategies();
  }, [loadStrategies]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    if (!user) return;
    loadPositions();
    loadPrefs();
  }, [user, loadPositions, loadPrefs]);

  const strategyOptions = useMemo(() => {
    const rows = strategies.map((row) => {
      const key = row.key || row.strategy_key;
      const label = row.name || row.label || key;
      return { key, label };
    }).filter((r) => !!r.key);
    const deduped = rows.filter((r, idx) => rows.findIndex((x) => x.key === r.key) === idx);
    if (deduped.some((r) => r.key === "__all_active__")) return deduped;
    return [{ key: "__all_active__", label: "All Active" }, ...deduped];
  }, [strategies]);

  const loadStrategyParams = useCallback(async () => {
    if (!isAdmin || !adminStrategyKey) return;
    setStrategyParamsLoading(true);
    setStrategyParamsErr("");
    try {
      const data = await fetchSignalStrategyParams(adminStrategyKey);
      const rows = toArray(data?.rows).map((row) => ({
        id: row?.id ?? null,
        param_name: String(row?.param_name ?? "").trim(),
        param_value:
          row?.param_value === undefined || row?.param_value === null
            ? ""
            : String(row.param_value),
      }));
      setStrategyParams(rows);
    } catch (e) {
      setStrategyParams([]);
      setStrategyParamsErr(e?.response?.data?.detail || "Could not load strategy params.");
    } finally {
      setStrategyParamsLoading(false);
    }
  }, [isAdmin, adminStrategyKey]);

  const loadBacktests = useCallback(async () => {
    if (!isAdmin) return;
    setBacktestsLoading(true);
    setBacktestsErr("");
    try {
      const data = await fetchSignalsBacktests({
        days_back: backtestsDaysBack,
        strategy_key: backtestsStrategy === "all" ? undefined : backtestsStrategy,
      });
      const rows = toArray(data?.rows || data?.backtests || data);
      setBacktestsRows(rows);
    } catch (e) {
      setBacktestsRows([]);
      setBacktestsErr(e?.response?.data?.detail || "Could not load backtests.");
    } finally {
      setBacktestsLoading(false);
    }
  }, [isAdmin, backtestsDaysBack, backtestsStrategy]);

  useEffect(() => {
    loadStrategyParams();
  }, [loadStrategyParams]);

  useEffect(() => {
    loadBacktests();
  }, [loadBacktests]);

  const currentSignalPriceBySignalId = useMemo(() => {
    const m = {};
    feed.open_signals.forEach((s) => {
      if (s.id != null && Number.isFinite(Number(s.market_price))) {
        m[String(s.id)] = Number(s.market_price);
      }
    });
    return m;
  }, [feed.open_signals]);

  const currentSignalPriceByFixtureId = useMemo(() => {
    const m = {};
    feed.open_signals.forEach((s) => {
      if (s.fixture_id != null && Number.isFinite(Number(s.market_price))) {
        m[String(s.fixture_id)] = Number(s.market_price);
      }
    });
    return m;
  }, [feed.open_signals]);

  const openPositions = useMemo(
    () =>
      positions.filter((p) => {
        const status = String(p.status || "").toLowerCase();
        return !p.closed_at && status !== "closed";
      }),
    [positions]
  );
  const closedPositions = useMemo(
    () => positions.filter((p) => !openPositions.includes(p)),
    [positions, openPositions]
  );

  const promptLogPosition = (signal) => {
    if (!user) {
      setFlash({ ok: false, message: "Log in to create a signal position." });
      return;
    }
    setLogModal({
      signal_id: signal.id || "",
      fixture_id: signal.fixture_id || "",
      strategy_key: signal.strategy_key || (strategyKey === "__all_active__" ? PRIMARY_STRATEGY : strategyKey) || PRIMARY_STRATEGY,
      platform: signal.recommended_platform || "Polymarket",
      market_id: signal.market_id || "",
      outcome_key: String(signal.recommended_outcome || "draw").toLowerCase(),
      entry_price: signal.market_price ?? "",
      entry_size: 1,
      target_exit_price: "",
      stop_price: "",
      notes: "",
    });
  };

  const savePosition = async (e) => {
    e.preventDefault();
    if (!logModal) return;
    setLogSaving(true);
    try {
      const payload = {
        signal_id: logModal.signal_id || null,
        fixture_id: logModal.fixture_id || null,
        strategy_key: logModal.strategy_key || DEFAULT_STRATEGY,
        platform: logModal.platform || null,
        market_id: logModal.market_id || null,
        outcome_key: logModal.outcome_key || "draw",
        entry_price: Number(logModal.entry_price),
        entry_size: Number(logModal.entry_size || 1),
        notes: logModal.notes || null,
      };
      if (!Number.isFinite(payload.entry_price)) {
        throw new Error("Entry price is required.");
      }
      if (logModal.target_exit_price !== "") payload.target_exit_price = Number(logModal.target_exit_price);
      if (logModal.stop_price !== "") payload.stop_price = Number(logModal.stop_price);
      await createSignalPosition(payload);
      setLogModal(null);
      setFlash({ ok: true, message: "Position logged." });
      loadPositions();
    } catch (e2) {
      setFlash({ ok: false, message: e2?.response?.data?.detail || e2.message || "Could not log position." });
    } finally {
      setLogSaving(false);
    }
  };

  const closePositionNow = async (positionId) => {
    const closeForm = closeFormById[positionId] || {};
    const closePrice = Number(closeForm.close_price);
    if (!Number.isFinite(closePrice)) {
      setFlash({ ok: false, message: "Close price is required." });
      return;
    }
    try {
      await closeSignalPosition(positionId, {
        close_price: closePrice,
        notes: closeForm.notes || null,
      });
      setFlash({ ok: true, message: "Position closed." });
      setCloseFormById((prev) => {
        const next = { ...prev };
        delete next[positionId];
        return next;
      });
      loadPositions();
    } catch (e) {
      setFlash({ ok: false, message: e?.response?.data?.detail || "Could not close position." });
    }
  };

  const savePrefs = async () => {
    if (!user) return;
    setPrefsSaving(true);
    setPrefsErr("");
    try {
      const payload = {
        ...prefs,
        min_edge: Number(prefs.min_edge || 0),
        default_target_exit:
          prefs.default_target_exit === "" ? null : Number(prefs.default_target_exit),
        favorite_sports: toArray(prefs.favorite_sports),
        favorite_strategies: toArray(prefs.favorite_strategies),
      };
      await updateSignalPreferences(payload);
      setFlash({ ok: true, message: "Signal preferences saved." });
      setPrefsSnapshot(JSON.stringify(payload));
      loadPrefs();
    } catch (e) {
      setPrefsErr(e?.response?.data?.detail || "Could not save preferences.");
    } finally {
      setPrefsSaving(false);
    }
  };

  const saveStrategyParams = async () => {
    if (!isAdmin || !adminStrategyKey) return;
    setStrategyParamsSaving(true);
    setStrategyParamsErr("");
    try {
      const trimmedRows = strategyParams
        .map((row) => ({
          param_name: String(row?.param_name || "").trim(),
          param_value:
            row?.param_value === undefined || row?.param_value === null
              ? ""
              : String(row.param_value),
        }))
        .filter((row) => row.param_name !== "");

      const invalidRow = trimmedRows.find((row) => row.param_value === "");
      if (invalidRow) {
        throw new Error(`Param value required for "${invalidRow.param_name}".`);
      }

      await updateSignalStrategyParams(adminStrategyKey, trimmedRows);
      setFlash({ ok: true, message: "Strategy params saved." });
      loadStrategyParams();
    } catch (e) {
      setStrategyParamsErr(e?.response?.data?.detail || "Could not save strategy params.");
      setFlash({ ok: false, message: "Strategy params save failed." });
    } finally {
      setStrategyParamsSaving(false);
    }
  };

  const refreshFeed = useCallback(async () => {
    await loadFeed();
  }, [loadFeed]);

  const refreshPositions = useCallback(async () => {
    await loadPositions();
  }, [loadPositions]);

  const runScanNow = async () => {
    setScanLoading(true);
    try {
      const data = await adminScanLiveSignals({
        strategy_key: strategyKey || DEFAULT_STRATEGY,
        notify_premium: notifyPremiumOnScan ? 1 : 0,
        include_all_leagues: includeAllLeaguesOnScan ? 1 : 0,
        max_staleness_sec: Number(maxStalenessSec || 180),
        include_stopped: includeStoppedFixtures ? 1 : 0,
      });
      await refreshFeed();
      setActionSummary((prev) => ({
        ...(prev || {}),
        evaluated: firstDefined(data?.evaluated, prev?.evaluated),
        triggered: firstDefined(data?.triggered, prev?.triggered),
        newly_created: firstDefined(data?.newly_created, prev?.newly_created),
        alerts_sent: firstDefined(data?.alerts_sent, prev?.alerts_sent),
        settled_backtests: firstDefined(data?.settled_backtests, data?.settled, prev?.settled_backtests),
        stale_skipped: firstDefined(data?.stale_skipped, prev?.stale_skipped),
        stopped_skipped: firstDefined(data?.stopped_skipped, prev?.stopped_skipped),
        watchlist_count: firstDefined(toArray(data?.watchlist).length, prev?.watchlist_count),
        last_updated_at: new Date().toISOString(),
        last_status: "scan-live",
      }));
      setFlash({ ok: true, message: "Scan complete and feed refreshed." });
    } catch (e) {
      setFlash({ ok: false, message: e?.response?.data?.detail || "Scan Live Signals failed." });
    } finally {
      setScanLoading(false);
    }
  };

  const runMonitorNow = async () => {
    setMonitorLoading(true);
    try {
      const data = await adminMonitorSignalExits();
      await Promise.all([refreshFeed(), refreshPositions()]);
      setActionSummary((prev) => ({
        ...(prev || {}),
        checked: firstDefined(data?.checked, prev?.checked),
        alerts_sent: firstDefined(data?.alerts_sent, prev?.alerts_sent),
        last_updated_at: new Date().toISOString(),
        last_status: "monitor-exits",
      }));
      setFlash({ ok: true, message: "Exit monitor complete. Feed and positions refreshed." });
    } catch (e) {
      setFlash({ ok: false, message: e?.response?.data?.detail || "Run Exit Monitor failed." });
    } finally {
      setMonitorLoading(false);
    }
  };

  const refreshSingleCard = async (cardKey) => {
    setRefreshingCardKey(cardKey);
    try {
      await adminScanLiveSignals({
        strategy_key: strategyKey || DEFAULT_STRATEGY,
        notify_premium: notifyPremiumOnScan ? 1 : 0,
        include_all_leagues: includeAllLeaguesOnScan ? 1 : 0,
        max_staleness_sec: Number(maxStalenessSec || 180),
        include_stopped: includeStoppedFixtures ? 1 : 0,
      });
      await refreshFeed();
      requestAnimationFrame(() => {
        const el = document.getElementById(`signal-card-${cardKey}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } catch (e) {
      setFlash({ ok: false, message: e?.response?.data?.detail || "Card refresh failed." });
    } finally {
      setRefreshingCardKey("");
    }
  };

  const openAdminSection = (sectionId) => {
    setTab("admin");
    requestAnimationFrame(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const hasUnsavedPrefs = useMemo(() => {
    if (!prefsSnapshot) return false;
    const current = JSON.stringify({
      ...prefs,
      min_edge: Number(prefs.min_edge || 0),
      default_target_exit:
        prefs.default_target_exit === "" ? null : Number(prefs.default_target_exit),
      favorite_sports: toArray(prefs.favorite_sports),
      favorite_strategies: toArray(prefs.favorite_strategies),
    });
    return current !== prefsSnapshot;
  }, [prefs, prefsSnapshot]);

  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedPrefs) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedPrefs]);

  return (
    <div style={S.page}>
      <section style={S.hero}>
        <div style={S.title}>Signals</div>
        <div style={S.sub}>
          Live model signals and near-trigger watchlist with one-click position logging.
          Current feed strategy: <b>{strategyKey}</b>.
        </div>
      </section>

      {flash && <div style={S.flash(!!flash.ok)}>{flash.message}</div>}

      <div style={S.tabs}>
        <button style={S.tab(tab === "feed")} onClick={() => setTab("feed")}>Signal Feed</button>
        <button style={S.tab(tab === "positions")} onClick={() => setTab("positions")}>My Signal Positions</button>
        <button style={S.tab(tab === "prefs")} onClick={() => setTab("prefs")}>Preferences</button>
        {isAdmin && <button style={S.tab(tab === "admin")} onClick={() => setTab("admin")}>Admin Lab</button>}
      </div>

      {tab === "feed" && (
        <>
          <section style={S.controls}>
            <div style={S.row}>
              <label style={S.muted}>Strategy</label>
              <select
                style={S.select}
                value={strategyKey}
                onChange={(e) => setStrategyKey(e.target.value)}
              >
                {(strategyOptions.length ? strategyOptions : [{ key: DEFAULT_STRATEGY, label: "All Active" }]).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button style={S.btn} onClick={refreshFeed} disabled={feedLoading}>
                {feedLoading ? "Refreshing..." : "Refresh Feed"}
              </button>
              <span style={{ ...S.muted, fontSize: 12 }} title="Fetch latest saved feed/watchlist">
                Refresh Feed: fetches saved feed/watchlist only
              </span>
              {isAdmin && (
                <>
                  <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={notifyPremiumOnScan}
                      onChange={(e) => setNotifyPremiumOnScan(e.target.checked)}
                    />
                    Notify premium users on new signals
                  </label>
                  <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={includeAllLeaguesOnScan}
                      onChange={(e) => setIncludeAllLeaguesOnScan(e.target.checked)}
                    />
                    Include all leagues
                  </label>
                  <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    Max staleness (sec)
                    <input
                      style={{ ...S.input, width: 90, height: 32 }}
                      type="number"
                      min={30}
                      step={30}
                      value={maxStalenessSec}
                      onChange={(e) => setMaxStalenessSec(Math.max(30, Number(e.target.value || 180)))}
                    />
                  </label>
                  <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={includeStoppedFixtures}
                      onChange={(e) => setIncludeStoppedFixtures(e.target.checked)}
                    />
                    Include stopped fixtures
                  </label>
                  <button
                    style={S.btn}
                    title="Run live strategy evaluation now and write signals"
                    onClick={runScanNow}
                    disabled={scanLoading}
                  >
                    {scanLoading ? "Scanning..." : "Scan Live Signals"}
                  </button>
                  <span style={{ ...S.muted, fontSize: 12 }}>
                    Scan Live Signals: executes fresh backend evaluation now
                  </span>
                  <button
                    style={S.btn}
                    title="Check open positions and send cash-out alerts"
                    onClick={runMonitorNow}
                    disabled={monitorLoading}
                  >
                    {monitorLoading ? "Running..." : "Run Exit Monitor"}
                  </button>
                  <span style={{ ...S.muted, fontSize: 12 }}>
                    Run Exit Monitor: checks open positions for exit alerts
                  </span>
                  <button style={S.btn} onClick={() => openAdminSection("signals-admin-performance")}>View Performance</button>
                  <button style={S.btn} onClick={() => openAdminSection("signals-admin-backtests")}>View Backtests</button>
                </>
              )}
            </div>
            {actionSummary && (
              <div style={{ ...S.kv, marginTop: 8 }}>
                <div><span style={S.muted}>Evaluated</span><br /><b>{actionSummary.evaluated ?? "—"}</b></div>
                <div><span style={S.muted}>Triggered</span><br /><b>{actionSummary.triggered ?? "—"}</b></div>
                <div><span style={S.muted}>Newly created</span><br /><b>{actionSummary.newly_created ?? "—"}</b></div>
                <div><span style={S.muted}>Alerts sent</span><br /><b>{actionSummary.alerts_sent ?? "—"}</b></div>
                <div><span style={S.muted}>Settled backtests</span><br /><b>{actionSummary.settled_backtests ?? "—"}</b></div>
                <div><span style={S.muted}>Checked</span><br /><b>{actionSummary.checked ?? "—"}</b></div>
                <div><span style={S.muted}>Stale skipped</span><br /><b>{actionSummary.stale_skipped ?? "—"}</b></div>
                <div><span style={S.muted}>Stopped skipped</span><br /><b>{actionSummary.stopped_skipped ?? "—"}</b></div>
                <div><span style={S.muted}>Last status</span><br /><b>{actionSummary.last_status || "—"}</b></div>
                <div><span style={S.muted}>Last scan time</span><br /><b>{actionSummary.last_updated_at ? new Date(actionSummary.last_updated_at).toLocaleTimeString() : "—"}</b></div>
              </div>
            )}
          </section>

          <section style={S.section}>
            <div style={S.sectionTitle}>Triggered Signals</div>
            {feedErr && (
              <div style={S.flash(false)}>
                {feedErr} <button style={S.btn} onClick={loadFeed}>Retry</button>
              </div>
            )}
            {!feedErr && !!feedWarning && (
              <div style={S.flash(false)}>{feedWarning}</div>
            )}
            {!feedErr && !feedLoading && !feed.open_signals.length && (
              <div style={S.muted}>No triggered signals right now.</div>
            )}
            <div style={S.grid}>
              {feed.open_signals.map((s) => {
                const cardKey = `triggered-${s.fixture_id || "na"}-${s.id || "na"}`;
                return (
                <article id={`signal-card-${cardKey}`} key={`sig-${s.id}-${s.fixture_id}`} style={S.card}>
                  <div style={{ ...S.row, justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 760 }}>{s.home_team} vs {s.away_team}</div>
                    <span style={S.badge}>Minute {s.minute ?? "—"}</span>
                  </div>
                  <div style={{ ...S.row, marginTop: 6 }}>
                    {s.strategy_id ? <span style={S.badge}>#{s.strategy_id}</span> : null}
                    {s.data_quality ? (
                      <span style={S.badge}>Data: {String(s.data_quality).toLowerCase()}</span>
                    ) : null}
                    <span style={S.badge}>Latency: {Number.isFinite(Number(s.latency_ms)) ? `${Math.round(Number(s.latency_ms))}ms` : "—"}</span>
                    <span style={S.badge}>Confidence score: {Number.isFinite(Number(s.confidence_score)) ? `${Math.round(Number(s.confidence_score))}` : "—"}</span>
                  </div>
                  <div style={{ ...S.muted, marginTop: 4, fontSize: 12 }}>
                    {prettyCompetitionName(s.league || "", "Football")} • Score {s.scoreline}
                  </div>
                  <div style={{ ...S.muted, marginTop: 4, fontSize: 12 }}>
                    Favourite (pre-match): {normFav(s.favourite_team_name)}
                  </div>

                  <div style={S.kv}>
                    <div><span style={S.muted}>Outcome</span><br /><b>{String(s.recommended_outcome || "draw").toUpperCase()}</b></div>
                    <div><span style={S.muted}>Platform</span><br /><b>{s.recommended_platform || "Polymarket"}</b></div>
                    <div><span style={S.muted}>Price</span><br /><b>{fmtNum(s.market_price, 3)}</b></div>
                    <div><span style={S.muted}>Model Prob</span><br /><b>{fmtPct(s.model_true_probability, 1)}</b></div>
                    <div><span style={S.muted}>Edge</span><br /><b>{fmtPct(s.edge, 1)}</b></div>
                    <div><span style={S.muted}>Confidence</span><br /><b>{fmtPct(s.confidence, 0)}</b></div>
                  </div>

                  <div style={{ ...S.kv, marginTop: 8 }}>
                    <div>
                      <span style={S.muted} title="Shown from favourite-minus-opponent perspective">xG diff</span>
                      <br />
                      <b style={{ color: diffColor(s.xg_diff) }}>{fmtNum(s.xg_diff, 2)}</b>
                      <br />
                      <span style={{ ...S.muted, fontSize: 11 }}>xG favors: {normFav(s.xg_favors_team)}</span>
                    </div>
                    <div>
                      <span style={S.muted} title="Estimated by 15-min cumulative live segments with event fallback">SoT15 diff</span>
                      <br />
                      <b style={{ color: diffColor(s.sot15_diff) }}>{fmtNum(s.sot15_diff, 2)}</b>
                      <br />
                      <span style={{ ...S.muted, fontSize: 11 }}>SoT15 favors: {normFav(s.sot15_favors_team)}</span>
                    </div>
                    <div>
                      <span style={S.muted}>Pressure proxy</span>
                      <br />
                      <b style={{ color: diffColor(s.pressure_proxy) }}>{fmtNum(s.pressure_proxy, 2)}</b>
                    </div>
                  </div>

                  <div style={{ ...S.row, marginTop: 10 }}>
                    {isPremium && prefs.push_enabled && (
                      <span style={{ ...S.badge, borderColor: "rgba(52,211,153,.5)", color: "#86efac" }}>
                        Premium Alert Eligible
                      </span>
                    )}
                    {!!s.market_url && (
                      <a href={s.market_url} target="_blank" rel="noreferrer" style={S.btnPrimary}>
                        Open Market
                      </a>
                    )}
                    <button style={S.btn} onClick={() => promptLogPosition(s)}>
                      Log Position
                    </button>
                    <button
                      style={S.btn}
                      onClick={() => refreshSingleCard(cardKey)}
                      disabled={refreshingCardKey === cardKey}
                      title="Runs scan-live with current toggles and then refreshes feed"
                    >
                      {refreshingCardKey === cardKey ? "Refreshing..." : "Refresh Card"}
                    </button>
                  </div>
                </article>
              );
              })}
            </div>
          </section>

          <section style={S.section}>
            <div style={S.sectionTitle}>Approaching Signal Zone</div>
            {!feedLoading && !feed.watchlist.length && (
              <div style={S.muted}>
                {actionSummary?.last_status === "scan-live"
                  ? "No fresh live fixtures met staleness rules."
                  : "No watchlist matches currently near trigger."}
              </div>
            )}
            <div style={S.grid}>
              {feed.watchlist.map((w) => {
                const p = clamp01(w.progress);
                const cardKey = `watch-${w.fixture_id || "na"}-${w.id || "na"}`;
                return (
                  <article id={`signal-card-${cardKey}`} key={`watch-${w.fixture_id}-${w.id}`} style={S.card}>
                    <div style={{ ...S.row, justifyContent: "space-between" }}>
                      <div style={{ fontWeight: 760 }}>{w.home_team} vs {w.away_team}</div>
                      <span style={S.badge}>Minute {w.minute ?? "—"}</span>
                    </div>
                    <div style={{ ...S.row, marginTop: 6 }}>
                      {w.data_quality ? <span style={S.badge}>Data: {String(w.data_quality).toLowerCase()}</span> : null}
                      <span style={S.badge}>Latency: {Number.isFinite(Number(w.latency_ms)) ? `${Math.round(Number(w.latency_ms))}ms` : "—"}</span>
                    </div>
                    <div style={{ ...S.muted, marginTop: 4, fontSize: 12 }}>
                      {prettyCompetitionName(w.league || "", "Football")} • Score {w.scoreline}
                    </div>
                    <div style={{ ...S.muted, marginTop: 4, fontSize: 12 }}>
                      Favourite (pre-match): {normFav(w.favourite_team_name)}
                    </div>
                    <div style={{ marginTop: 9 }}>
                      <div style={{ ...S.row, justifyContent: "space-between", fontSize: 12 }}>
                        <span style={S.muted}>Progress to trigger</span>
                        <b>{Math.round(p * 100)}%</b>
                      </div>
                      <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,.1)", marginTop: 4 }}>
                        <div
                          style={{
                            width: `${Math.round(p * 100)}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: "linear-gradient(90deg,#31d48b,#14b567)",
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 9, fontSize: 12 }}>
                      <span style={S.muted}>Reason</span><br />
                      <b>{w.reason || "Near strategy threshold."}</b>
                    </div>
                    <div style={{ ...S.kv, marginTop: 8 }}>
                      <div>
                        <span style={S.muted} title="Shown from favourite-minus-opponent perspective">xG diff</span>
                        <br />
                        <b style={{ color: diffColor(w.xg_diff) }}>{fmtNum(w.xg_diff, 2)}</b>
                        <br />
                        <span style={{ ...S.muted, fontSize: 11 }}>xG favors: {normFav(w.xg_favors_team)}</span>
                      </div>
                      <div>
                        <span style={S.muted} title="Estimated by 15-min cumulative live segments with event fallback">SoT15 diff</span>
                        <br />
                        <b style={{ color: diffColor(w.sot15_diff) }}>{fmtNum(w.sot15_diff, 2)}</b>
                        <br />
                        <span style={{ ...S.muted, fontSize: 11 }}>SoT15 favors: {normFav(w.sot15_favors_team)}</span>
                      </div>
                      <div>
                        <span style={S.muted}>Pressure proxy</span>
                        <br />
                        <b style={{ color: diffColor(w.pressure_proxy) }}>{fmtNum(w.pressure_proxy, 2)}</b>
                      </div>
                    </div>
                    <div style={{ ...S.row, marginTop: 10 }}>
                      <button
                        style={S.btn}
                        onClick={() => refreshSingleCard(cardKey)}
                        disabled={refreshingCardKey === cardKey}
                        title="Runs scan-live with current toggles and then refreshes feed"
                      >
                        {refreshingCardKey === cardKey ? "Refreshing..." : "Refresh Card"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}

      {tab === "positions" && (
        <section style={S.section}>
          <div style={S.row}>
            <div style={S.sectionTitle}>My Signal Positions</div>
            <button style={S.btn} onClick={loadPositions} disabled={positionsLoading}>
              {positionsLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          {!user ? (
            <div style={S.muted}>
              <Link to="/login" style={{ color: "#9be7ff" }}>Log in</Link> to manage signal positions.
            </div>
          ) : (
            <>
              {positionsErr && (
                <div style={S.flash(false)}>
                  {positionsErr} <button style={S.btn} onClick={loadPositions}>Retry</button>
                </div>
              )}
              {!positionsErr && !openPositions.length && !closedPositions.length && (
                <div style={S.muted}>No positions yet. Log one from the Signal Feed.</div>
              )}

              {!!openPositions.length && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Open</div>
                  <div style={S.grid}>
                    {openPositions.map((p) => {
                      const entry = Number(p.entry_price);
                      const size = Number(p.entry_size || 1);
                      const cur =
                        currentSignalPriceBySignalId[String(p.signal_id)] ??
                        currentSignalPriceByFixtureId[String(p.fixture_id)];
                      const unrealized =
                        Number.isFinite(entry) && Number.isFinite(size) && Number.isFinite(cur)
                          ? (cur - entry) * size
                          : null;
                      return (
                        <article key={p.id} style={S.card}>
                          <div style={{ ...S.row, justifyContent: "space-between" }}>
                            <div style={{ fontWeight: 760 }}>{p.strategy_key || DEFAULT_STRATEGY}</div>
                            <span style={S.badge}>Open</span>
                          </div>
                          <div style={{ ...S.kv, marginTop: 8 }}>
                            <div><span style={S.muted}>Outcome</span><br /><b>{String(p.outcome_key || "draw").toUpperCase()}</b></div>
                            <div><span style={S.muted}>Platform</span><br /><b>{p.platform || "—"}</b></div>
                            <div><span style={S.muted}>Entry</span><br /><b>{fmtNum(p.entry_price, 3)}</b></div>
                            <div><span style={S.muted}>Size</span><br /><b>{fmtNum(p.entry_size, 2)}</b></div>
                            <div><span style={S.muted}>Unrealized est.</span><br />
                              <b style={{ color: unrealized > 0 ? "#34d399" : unrealized < 0 ? "#f87171" : "#eaf4ed" }}>
                                {unrealized == null ? "—" : `${unrealized >= 0 ? "+" : ""}${fmtNum(unrealized, 2)}u`}
                              </b>
                            </div>
                          </div>
                          <div style={{ ...S.row, marginTop: 10 }}>
                            <input
                              style={{ ...S.input, minWidth: 120 }}
                              placeholder="Close price"
                              value={closeFormById[p.id]?.close_price || ""}
                              onChange={(e) =>
                                setCloseFormById((prev) => ({
                                  ...prev,
                                  [p.id]: { ...prev[p.id], close_price: e.target.value },
                                }))
                              }
                            />
                            <input
                              style={{ ...S.input, minWidth: 180, flex: 1 }}
                              placeholder="Notes (optional)"
                              value={closeFormById[p.id]?.notes || ""}
                              onChange={(e) =>
                                setCloseFormById((prev) => ({
                                  ...prev,
                                  [p.id]: { ...prev[p.id], notes: e.target.value },
                                }))
                              }
                            />
                            <button style={S.btnPrimary} onClick={() => closePositionNow(p.id)}>
                              Close
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {!!closedPositions.length && (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Closed</div>
                  <div style={S.grid}>
                    {closedPositions.map((p) => {
                      const realized =
                        Number.isFinite(Number(p.realized_pl_units))
                          ? Number(p.realized_pl_units)
                          : Number.isFinite(Number(p.close_price)) && Number.isFinite(Number(p.entry_price))
                          ? (Number(p.close_price) - Number(p.entry_price)) * Number(p.entry_size || 1)
                          : null;
                      return (
                        <article key={p.id} style={S.card}>
                          <div style={{ ...S.row, justifyContent: "space-between" }}>
                            <div style={{ fontWeight: 760 }}>{p.strategy_key || DEFAULT_STRATEGY}</div>
                            <span style={S.badge}>Closed</span>
                          </div>
                          <div style={S.kv}>
                            <div><span style={S.muted}>Entry</span><br /><b>{fmtNum(p.entry_price, 3)}</b></div>
                            <div><span style={S.muted}>Close</span><br /><b>{fmtNum(p.close_price, 3)}</b></div>
                            <div><span style={S.muted}>Size</span><br /><b>{fmtNum(p.entry_size, 2)}</b></div>
                            <div><span style={S.muted}>P/L units</span><br />
                              <b style={{ color: realized > 0 ? "#34d399" : realized < 0 ? "#f87171" : "#eaf4ed" }}>
                                {realized == null ? "—" : `${realized >= 0 ? "+" : ""}${fmtNum(realized, 2)}u`}
                              </b>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {tab === "prefs" && (
        <section style={S.section}>
          <div style={S.sectionTitle}>Alert Preferences</div>
          {!user ? (
            <div style={S.muted}>
              <Link to="/login" style={{ color: "#9be7ff" }}>Log in</Link> to configure alerts.
            </div>
          ) : (
            <>
              {prefsErr && (
                <div style={S.flash(false)}>
                  {prefsErr} <button style={S.btn} onClick={loadPrefs}>Retry</button>
                </div>
              )}
              {prefsLoading ? (
                <div style={S.muted}>Loading preferences...</div>
              ) : (
                <fieldset style={{ display: "grid", gap: 10, border: 0, margin: 0, padding: 0 }} disabled={prefsSaving}>
                  <label style={S.row}>
                    <input
                      type="checkbox"
                      checked={!!prefs.push_enabled}
                      onChange={(e) => setPrefs((p) => ({ ...p, push_enabled: e.target.checked }))}
                    />
                    Push enabled
                  </label>
                  <label style={S.row}>
                    <input
                      type="checkbox"
                      checked={!!prefs.email_enabled}
                      onChange={(e) => setPrefs((p) => ({ ...p, email_enabled: e.target.checked }))}
                    />
                    Email enabled
                  </label>

                  <div style={S.row}>
                    <label style={S.muted}>Min edge</label>
                    <input
                      style={{ ...S.input, width: 140 }}
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={prefs.min_edge ?? 0}
                      onChange={(e) => setPrefs((p) => ({ ...p, min_edge: e.target.value }))}
                    />
                    <span style={S.muted}>({fmtPct(Number(prefs.min_edge || 0), 2)})</span>
                  </div>
                  <div style={{ ...S.muted, fontSize: 12 }}>
                    Set threshold between 0 and 1. Example: 0.03 means 3.00% minimum edge.
                  </div>

                  <div style={S.row}>
                    <label style={S.muted}>Default target exit</label>
                    <input
                      style={{ ...S.input, width: 140 }}
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={prefs.default_target_exit ?? ""}
                      onChange={(e) => setPrefs((p) => ({ ...p, default_target_exit: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label style={S.muted}>Favorite sports (comma separated)</label>
                    <input
                      style={{ ...S.input, width: "100%", marginTop: 4 }}
                      value={toArray(prefs.favorite_sports).join(", ")}
                      onChange={(e) =>
                        setPrefs((p) => ({
                          ...p,
                          favorite_sports: e.target.value
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean),
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label style={S.muted}>Favorite strategies (comma separated)</label>
                    {strategies.length ? (
                      <div style={{ marginTop: 6, display: "grid", gap: 6 }}>
                        {strategies.map((row) => {
                          const key = row.key || row.strategy_key;
                          const checked = toArray(prefs.favorite_strategies).includes(key);
                          return (
                            <label key={key} style={{ ...S.row, fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) =>
                                  setPrefs((p) => ({
                                    ...p,
                                    favorite_strategies: e.target.checked
                                      ? [...new Set([...toArray(p.favorite_strategies), key])]
                                      : toArray(p.favorite_strategies).filter((x) => x !== key),
                                  }))
                                }
                              />
                              {row.name || key}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <input
                        style={{ ...S.input, width: "100%", marginTop: 4 }}
                        value={toArray(prefs.favorite_strategies).join(", ")}
                        onChange={(e) =>
                          setPrefs((p) => ({
                            ...p,
                            favorite_strategies: e.target.value
                              .split(",")
                              .map((x) => x.trim())
                              .filter(Boolean),
                          }))
                        }
                      />
                    )}
                  </div>

                  <div style={{ marginTop: 6 }}>
                    <button style={S.btnPrimary} onClick={savePrefs} disabled={prefsSaving}>
                      {prefsSaving ? "Saving..." : "Save Preferences"}
                    </button>
                    {hasUnsavedPrefs && (
                      <span style={{ marginLeft: 10, fontSize: 12, color: "#fbbf24" }}>
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                </fieldset>
              )}
            </>
          )}
        </section>
      )}

      {tab === "admin" && isAdmin && (
        <>
          <section id="signals-admin-performance" style={S.section}>
            <div style={S.row}>
              <div style={S.sectionTitle}>Strategy Config</div>
              <select
                style={S.select}
                value={adminStrategyKey}
                onChange={(e) => setAdminStrategyKey(e.target.value)}
              >
                {strategies.map((row, i) => {
                  const key = row.key || row.strategy_key || `strategy-${i}`;
                  const label = row.name || row.label || key;
                  return (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <button style={S.btn} onClick={loadStrategyParams} disabled={strategyParamsLoading}>
                {strategyParamsLoading ? "Loading..." : "Load Params"}
              </button>
            </div>

            {strategyParamsErr && (
              <div style={S.flash(false)}>
                {strategyParamsErr}
              </div>
            )}

            <div style={{ display: "grid", gap: 8 }}>
              {strategyParams.map((row, idx) => (
                <div key={`param-${idx}`} style={S.row}>
                  <input
                    style={{ ...S.input, minWidth: 220 }}
                    value={row.param_name}
                    onChange={(e) =>
                      setStrategyParams((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, param_name: e.target.value } : item))
                      )
                    }
                  />
                  <input
                    style={{ ...S.input, flex: 1, minWidth: 220 }}
                    value={row.param_value}
                    onChange={(e) =>
                      setStrategyParams((prev) =>
                        prev.map((item, i) => (i === idx ? { ...item, param_value: e.target.value } : item))
                      )
                    }
                  />
                  <button
                    type="button"
                    style={S.btn}
                    onClick={() =>
                      setStrategyParams((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
              {!strategyParamsLoading && !strategyParams.length && (
                <div style={{ ...S.muted, fontSize: 13 }}>
                  No overrides yet (using strategy defaults).
                </div>
              )}
            </div>

            <div style={{ ...S.row, marginTop: 10 }}>
              <button
                type="button"
                style={S.btn}
                onClick={() =>
                  setStrategyParams((prev) => [
                    ...prev,
                    { id: null, param_name: "", param_value: "" },
                  ])
                }
              >
                Add Row
              </button>
              <button
                type="button"
                style={S.btnPrimary}
                onClick={saveStrategyParams}
                disabled={strategyParamsSaving}
              >
                {strategyParamsSaving ? "Saving..." : "Save Params"}
              </button>
            </div>
          </section>

          <section id="signals-admin-backtests" style={S.section}>
            <div style={S.row}>
              <div style={S.sectionTitle}>Backtests</div>
              <label style={S.row}>
                <span style={S.muted}>Days</span>
                <select
                  style={{ ...S.select, minWidth: 100 }}
                  value={backtestsDaysBack}
                  onChange={(e) => setBacktestsDaysBack(Number(e.target.value))}
                >
                  {[30, 60, 90, 180].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <label style={S.row}>
                <span style={S.muted}>Strategy</span>
                <select
                  style={S.select}
                  value={backtestsStrategy}
                  onChange={(e) => setBacktestsStrategy(e.target.value)}
                >
                  <option value="all">All</option>
                  {strategies.map((row, i) => {
                    const key = row.key || row.strategy_key || `strategy-${i}`;
                    const label = row.name || row.label || key;
                    return (
                      <option key={key} value={key}>{label}</option>
                    );
                  })}
                </select>
              </label>
              <button style={S.btn} onClick={loadBacktests} disabled={backtestsLoading}>
                {backtestsLoading ? "Loading..." : "Refresh"}
              </button>
            </div>

            {backtestsErr && (
              <div style={S.flash(false)}>
                {backtestsErr}
              </div>
            )}

            <div style={{ overflowX: "auto", marginTop: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Strategy", "fixture_id", "fired_minute", "entry_price", "exit_price", "outcome", "roi", "created_at"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === "Strategy" || h === "outcome" || h === "created_at" ? "left" : "right",
                          padding: "8px 6px",
                          borderBottom: "1px solid rgba(148,163,184,0.3)",
                          color: "#cbd5e1",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {backtestsRows.map((row, idx) => (
                    <tr key={`backtest-${idx}`}>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {row.strategy || row.strategy_key || row.strategy_id || "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {row.fixture_id ?? "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {row.fired_minute ?? "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {fmtNum(row.entry_price, 3)}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {fmtNum(row.exit_price, 3)}
                      </td>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {row.outcome || "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "right", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {fmtPct(row.roi, 2)}
                      </td>
                      <td style={{ padding: "8px 6px", borderBottom: "1px solid rgba(148,163,184,0.18)" }}>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                  {!backtestsLoading && !backtestsRows.length && (
                    <tr>
                      <td colSpan={8} style={{ padding: 10, color: "#94a3b8" }}>
                        No backtests found for selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {logModal && (
        <div style={S.modalBackdrop} onClick={() => setLogModal(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...S.sectionTitle, marginBottom: 8 }}>Log Position</div>
            <form onSubmit={savePosition} style={{ display: "grid", gap: 8 }}>
              <div style={S.row}>
                <input style={{ ...S.input, flex: 1 }} value={logModal.signal_id ?? ""} readOnly />
                <input style={{ ...S.input, flex: 1 }} value={logModal.fixture_id ?? ""} readOnly />
              </div>
              <div style={S.row}>
                <input
                  style={{ ...S.input, flex: 1 }}
                  value={logModal.strategy_key}
                  onChange={(e) => setLogModal((p) => ({ ...p, strategy_key: e.target.value }))}
                />
                <input
                  style={{ ...S.input, flex: 1 }}
                  value={logModal.platform}
                  onChange={(e) => setLogModal((p) => ({ ...p, platform: e.target.value }))}
                />
              </div>
              <div style={S.row}>
                <input
                  style={{ ...S.input, flex: 1 }}
                  placeholder="Market ID"
                  value={logModal.market_id}
                  onChange={(e) => setLogModal((p) => ({ ...p, market_id: e.target.value }))}
                />
                <input
                  style={{ ...S.input, flex: 1 }}
                  placeholder="Outcome key"
                  value={logModal.outcome_key}
                  onChange={(e) => setLogModal((p) => ({ ...p, outcome_key: e.target.value }))}
                />
              </div>
              <div style={S.row}>
                <input
                  style={{ ...S.input, flex: 1 }}
                  type="number"
                  step="0.001"
                  required
                  placeholder="Entry price"
                  value={logModal.entry_price}
                  onChange={(e) => setLogModal((p) => ({ ...p, entry_price: e.target.value }))}
                />
                <input
                  style={{ ...S.input, flex: 1 }}
                  type="number"
                  step="0.01"
                  placeholder="Entry size"
                  value={logModal.entry_size}
                  onChange={(e) => setLogModal((p) => ({ ...p, entry_size: e.target.value }))}
                />
              </div>
              <div style={S.row}>
                <input
                  style={{ ...S.input, flex: 1 }}
                  type="number"
                  step="0.001"
                  placeholder="Target exit (optional)"
                  value={logModal.target_exit_price}
                  onChange={(e) => setLogModal((p) => ({ ...p, target_exit_price: e.target.value }))}
                />
                <input
                  style={{ ...S.input, flex: 1 }}
                  type="number"
                  step="0.001"
                  placeholder="Stop price (optional)"
                  value={logModal.stop_price}
                  onChange={(e) => setLogModal((p) => ({ ...p, stop_price: e.target.value }))}
                />
              </div>
              <textarea
                rows={3}
                style={S.textarea}
                placeholder="Notes"
                value={logModal.notes}
                onChange={(e) => setLogModal((p) => ({ ...p, notes: e.target.value }))}
              />
              <div style={{ ...S.row, justifyContent: "flex-end" }}>
                <button type="button" style={S.btn} onClick={() => setLogModal(null)}>Cancel</button>
                <button type="submit" style={S.btnPrimary} disabled={logSaving}>
                  {logSaving ? "Saving..." : "Save Position"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
